import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { tokenStore } from "@/lib/token";
import axios from "axios";
import { 
  CheckCircle2, 
  XCircle, 
  Loader2,
  Package,
  ArrowRight,
  RefreshCw
} from "lucide-react";
import Navbar from "@/components/Navbar";

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const token = tokenStore.get();
        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPaymentData(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('error');
        } else if (pollCount < 10) {
          // Continue polling
          setTimeout(() => {
            setPollCount(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        if (pollCount < 5) {
          setTimeout(() => {
            setPollCount(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-vanta-base">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <div className="bg-vanta-surface border border-border-default p-12 text-center">
            {status === 'loading' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Loader2 className="w-16 h-16 text-pulse-red animate-spin" />
                </div>
                <h1 className="font-heading text-2xl font-bold tracking-tighter mb-4">
                  Vérification du paiement...
                </h1>
                <p className="text-text-secondary mb-6">
                  Veuillez patienter pendant que nous confirmons votre paiement.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-text-muted">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Tentative {pollCount + 1}/10</span>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-status-success/10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-status-success" />
                </div>
                <h1 className="font-heading text-2xl font-bold tracking-tighter mb-4">
                  {paymentData?.free ? "Commande validée !" : "Paiement réussi !"}
                </h1>
                <p className="text-text-secondary mb-8">
                  Votre produit est activé. Retrouvez-le dans votre tableau de bord : gestion de votre bot, ou clé de licence et téléchargement pour un script.
                </p>
                
                <div className="bg-vanta-base border border-border-default p-6 mb-8">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-vanta-surface border border-border-default flex items-center justify-center">
                      <Package className="w-7 h-7 text-pulse-red" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold">Produit activé</p>
                      <p className="text-sm text-text-muted">Statut: Actif</p>
                    </div>
                  </div>
                  
                  {paymentData && typeof paymentData.amount_total === 'number' && (
                    <div className="text-sm text-text-secondary">
                      <p>Montant payé : {(paymentData.amount_total / 100).toFixed(2)}€{paymentData.free ? " (offert)" : ""}</p>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    data-testid="go-to-dashboard-btn"
                    onClick={() => navigate('/dashboard')}
                    className="bg-pulse-red hover:bg-pulse-red-hover text-white px-8 py-3 rounded-none btn-hover-lift"
                  >
                    Voir mes produits
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button 
                    data-testid="add-another-btn"
                    variant="outline"
                    onClick={() => navigate('/shop')}
                    className="border-border-default hover:bg-vanta-base px-8 py-3 rounded-none"
                  >
                    Découvrir d'autres produits
                  </Button>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-status-error/10 rounded-full flex items-center justify-center">
                  <XCircle className="w-12 h-12 text-status-error" />
                </div>
                <h1 className="font-heading text-2xl font-bold tracking-tighter mb-4">
                  Problème de paiement
                </h1>
                <p className="text-text-secondary mb-8">
                  Une erreur s'est produite lors de la vérification du paiement. 
                  Si vous avez été débité, veuillez contacter notre support.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    data-testid="retry-btn"
                    onClick={() => navigate('/checkout')}
                    className="bg-pulse-red hover:bg-pulse-red-hover text-white px-8 py-3 rounded-none btn-hover-lift"
                  >
                    Réessayer
                  </Button>
                  <Button 
                    data-testid="contact-support-btn"
                    variant="outline"
                    onClick={() => window.location.href = 'mailto:support@vantapulse.fr'}
                    className="border-border-default hover:bg-vanta-base px-8 py-3 rounded-none"
                  >
                    Contacter le support
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
